import React, { useState, useEffect } from 'react';
import { getLevelInfo, checkin } from '../services/level';

/**
 * 레벨 카드 컴포넌트
 * - 사용자 드롭다운/프로필에서 상세 레벨 정보 표시
 * - 출석 체크 버튼 포함
 */
const LevelCard = ({ onLevelUp }) => {
  const [levelInfo, setLevelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinMessage, setCheckinMessage] = useState(null);

  const fetchLevelInfo = async () => {
    try {
      const data = await getLevelInfo();
      setLevelInfo(data);
    } catch (err) {
      console.error('Failed to fetch level info:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevelInfo();
  }, []);

  const handleCheckin = async () => {
    if (checkinLoading) return;

    setCheckinLoading(true);
    setCheckinMessage(null);

    try {
      const result = await checkin();
      setCheckinMessage({
        type: result.expGained > 0 ? 'success' : 'info',
        text: result.message
      });

      // 레벨업 시 콜백
      if (result.levelUp && onLevelUp) {
        onLevelUp(result.newLevel);
      }

      // 레벨 정보 새로고침
      await fetchLevelInfo();

      // 3초 후 메시지 제거
      setTimeout(() => setCheckinMessage(null), 3000);
    } catch (err) {
      setCheckinMessage({
        type: 'error',
        text: '출석 체크에 실패했습니다'
      });
    } finally {
      setCheckinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 bg-gradient-to-br from-[#fafbfc] to-[#f5f7fa] rounded-[10px] border border-[#e2e8f0] min-h-[180px]">
        <div className="w-full h-full bg-gradient-to-r from-[#f1f5f9] via-[#e2e8f0] to-[#f1f5f9] bg-[length:200%_100%] animate-[skeleton-loading_1.5s_infinite] rounded-lg" />
      </div>
    );
  }

  if (!levelInfo) {
    return null;
  }

  const { level, currentExp, nextLevelExp, totalExp, progress, title, stats } = levelInfo;

  return (
    <div className="p-3 bg-gradient-to-br from-[#fafbfc] to-[#f5f7fa] rounded-[10px] border border-[#e2e8f0]">
      {/* 레벨 헤더 */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="text-[28px] leading-none">{title?.icon || '👀'}</div>
        <div className="flex flex-col gap-px">
          <div className="text-base max-md:text-lg font-bold text-[#1a1a1a]">Lv.{level}</div>
          <div className="text-xs text-[#64748b] font-medium">{title?.name || '문파 구경꾼'}</div>
        </div>
      </div>

      {/* 경험치 바 */}
      <div className="mb-2.5">
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-[#64748b] font-medium">경험치</span>
          <span className="text-[11px] text-[#3b82f6] font-semibold">
            {currentExp.toLocaleString()} / {nextLevelExp.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 bg-[#e2e8f0] rounded-[3px] overflow-hidden mb-1">
          <div
            className="h-full bg-gradient-to-r from-[#3b82f6] via-[#60a5fa] to-[#93c5fd] rounded transition-[width] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] relative after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-r after:from-transparent after:via-white/30 after:to-transparent after:animate-[shimmer_2s_infinite]"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="text-[11px] text-[#94a3b8] text-right">
          총 {totalExp.toLocaleString()} EXP
        </div>
      </div>

      {/* 통계 */}
      <div className="flex gap-2 mb-2.5 p-2 max-md:p-2.5 bg-white rounded-md border border-[#f1f5f9]">
        <div className="flex-1 flex flex-col items-center gap-px">
          <span className="text-sm">🔥</span>
          <span className="text-[13px] max-md:text-sm font-bold text-[#1a1a1a]">{stats?.loginStreak || 0}일</span>
          <span className="text-[9px] text-[#94a3b8] font-medium">연속 접속</span>
        </div>
        <div className="flex-1 flex flex-col items-center gap-px">
          <span className="text-sm">📅</span>
          <span className="text-[13px] max-md:text-sm font-bold text-[#1a1a1a]">{stats?.totalLogins || 0}회</span>
          <span className="text-[9px] text-[#94a3b8] font-medium">총 접속</span>
        </div>
      </div>

      {/* 출석 체크 버튼 */}
      <button
        className={`w-full py-2 px-3 bg-gradient-to-br from-[#3b82f6] to-[#2563eb] text-white border-none rounded-md text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 hover:bg-gradient-to-br hover:from-[#2563eb] hover:to-[#1d4ed8] hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(59,130,246,0.3)] active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed ${
          checkinLoading ? 'bg-[#94a3b8]' : ''
        }`}
        onClick={handleCheckin}
        disabled={checkinLoading}
      >
        {checkinLoading ? (
          <span className="text-white/80">처리 중...</span>
        ) : (
          <>
            <span className="text-sm">✅</span>
            <span>출석 체크</span>
          </>
        )}
      </button>

      {/* 출석 메시지 */}
      {checkinMessage && (
        <div
          className={`mt-2 py-1.5 px-2.5 rounded-md text-[11px] font-medium text-center animate-[fadeInUp_0.3s_ease] ${
            checkinMessage.type === 'success'
              ? 'bg-[#dcfce7] text-[#166534] border border-[#bbf7d0]'
              : checkinMessage.type === 'info'
              ? 'bg-[#dbeafe] text-[#1e40af] border border-[#bfdbfe]'
              : 'bg-[#fee2e2] text-[#991b1b] border border-[#fecaca]'
          }`}
        >
          {checkinMessage.text}
        </div>
      )}
    </div>
  );
};

export default LevelCard;
